import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { supabase } from '../../lib/supabase';
import styles from './SearchModal.module.css';
import { useStoreCurrency } from '../../hooks/useStoreCurrency';

// Pravilno konvertiranje ikona u React komponente
const SearchIcon = FaSearch as React.ComponentType<any>;
const CloseIcon = FaTimes as React.ComponentType<any>;

interface Product {
  id: string;
  name: string;
  price: number;
  image_url: string;
  slug: string;
}

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { formatPrice } = useStoreCurrency();
  
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setSearchResults([]);
    }
  }, [isOpen]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.trim().length >= 2) {
        searchProducts();
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const searchProducts = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('products')
        .select('id, name, price, image_url, slug')
        .or(`description.ilike.%${searchTerm}%,name.ilike.%${searchTerm}%,short_description.ilike.%${searchTerm}%`)
        .order('name')
        .limit(8);

      if (error) throw error;
      setSearchResults(data || []);
    } catch (error) {
      console.error('Error searching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.searchHeader}>
          <div className={styles.searchInputWrapper}>
            <SearchIcon className={styles.searchIcon} />
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Pretražite proizvode..."
              className={styles.searchInput}
            />
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        <div className={styles.searchResults}>
          {loading ? (
            <div className={styles.loadingMessage}>Pretraživanje...</div>
          ) : searchResults.length > 0 ? (
            <div className={styles.resultsList}>
              {searchResults.map(product => (
                <Link
                  key={product.id}
                  to={`/p/${product.slug}`}
                  className={styles.resultItem}
                  onClick={onClose}
                >
                  <div className={styles.resultImageContainer}>
                    {product.image_url ? (
                      <img 
                        src={product.image_url}
                        alt={product.name}
                        className={styles.resultImage}
                      />
                    ) : (
                      <div className={styles.noImage}>No Image</div>
                    )}
                  </div>
                  <div className={styles.resultDetails}>
                    <h4 className={styles.resultName}>{product.name}</h4>
                    <p className={styles.resultPrice}>{formatPrice(product.price)}</p>
                  </div>
                </Link>
              ))}
              
              {searchResults.length > 0 && (
                <div className={styles.viewAllContainer}>
                  <Link 
                    to={`/search?q=${encodeURIComponent(searchTerm)}`}
                    className={styles.viewAllLink}
                    onClick={onClose}
                  >
                    Pogledajte sve rezultate
                  </Link>
                </div>
              )}
            </div>
          ) : searchTerm.length >= 2 ? (
            <div className={styles.noResults}>Nije pronađen nijedan proizvod</div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default SearchModal; 